import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, ShieldAlert, Star } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MillieAvatar, MillieHero } from "../components/MascotBubble";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const CHEERS = ["Great helping!", "Wow, look at you go!", "Super chef!", "You're a legend!", "Yum yum yum!"];

export default function KidMode() {
  const { day: dayParam } = useParams();
  const day = dayParam === "today" ? DAY_NAMES[new Date().getDay()] : dayParam;
  const { activeId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(-1);
  const [illustrating, setIllustrating] = useState(false);

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.post("/cook/kidmode", { household_id: activeId, day }); setData(data); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [activeId, day]);
  useEffect(() => { load(); }, [load]);

  const illustrate = async () => {
    setIllustrating(true);
    toast.loading("Millie's drawing the pictures — about a minute…", { id: "ill" });
    try { const { data } = await api.post("/cook/kidmode/illustrate", { household_id: activeId, day }); setData(data); toast.success(`${data.illustrated} pictures ready! 🎨`, { id: "ill" }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "ill" }); }
    finally { setIllustrating(false); }
  };

  const steps = data?.steps || [];
  const hasPics = steps.some((s) => s.image_url);
  const done = idx >= steps.length;

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF3D6] to-[#FFE3EC] pb-10 flex flex-col">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate(`/cook/${dayParam}`)} data-testid="kid-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-3xl text-sorted-ink">Little Chefs</h1>
          <span className="w-11" />
        </div>

        {error ? (
          <p className="px-6 mt-10 text-center font-hand text-2xl text-sorted-ink">{error}</p>
        ) : !data ? (
          <div className="px-6 mt-8 flex flex-col items-center gap-4"><MillieAvatar size={72} mood="thinking" /><p className="font-hand text-2xl text-sorted-ink animate-pulse">Making it kid-sized…</p></div>
        ) : idx === -1 ? (
          <div className="px-6 mt-4" data-testid="kid-intro">
            <div className="flex justify-center"><MillieAvatar size={110} mood="cooking" /></div>
            <h2 className="font-heading text-2xl font-bold text-sorted-ink text-center mt-3" data-testid="kid-title">Let's help make {data.title}!</h2>
            <p className="text-center text-sorted-slate text-sm mt-1">{steps.length} easy jobs. Grown-ups do the hot and sharp bits.</p>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {steps.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl p-2 text-center shadow-soft" data-testid={`kid-preview-${i}`}>
                  {s.image_url ? <img src={`${API_BASE}${s.image_url}`} alt="" className="w-full aspect-square object-cover rounded-xl" /> : <div className="w-full aspect-square rounded-xl bg-[#FFF6F1] flex items-center justify-center text-4xl">{s.emoji}</div>}
                </div>
              ))}
            </div>
            {!hasPics && (
              <button onClick={illustrate} disabled={illustrating} data-testid="kid-illustrate" className="mt-4 w-full bg-white border-2 border-[#FDECEF] text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2">
                {illustrating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2.5} />} Make it picture-led <span className="text-[10px] font-bold text-sorted-slate">(parents)</span>
              </button>
            )}
            <button onClick={() => setIdx(0)} data-testid="kid-start" className="mt-3 w-full bg-sorted-rose text-white font-bold py-4 text-lg rounded-full flex items-center justify-center gap-2">Let's go! <ArrowRight size={20} strokeWidth={2.5} /></button>
          </div>
        ) : done ? (
          <div className="px-6 mt-10 text-center" data-testid="kid-finish">
            <MillieHero mood="celebrate" className="h-52 w-44 mx-auto" testId="kid-finish-millie" />
            <h2 className="font-hand text-5xl text-sorted-ink mt-4">You did it!</h2>
            <p className="text-sorted-slate mt-2">Super chef of the night. High five! ✋</p>
            <div className="flex justify-center gap-1 mt-3">{[0, 1, 2].map((i) => <Star key={i} size={28} className="text-amber-400 fill-amber-400" />)}</div>
            <button onClick={() => navigate(`/cook/${dayParam}`)} data-testid="kid-done" className="mt-8 w-full bg-sorted-rose text-white font-bold py-4 rounded-full">Back to the grown-up recipe</button>
          </div>
        ) : (
          <KidStep step={steps[idx]} idx={idx} total={steps.length} onNext={() => setIdx(idx + 1)} onPrev={() => setIdx(idx - 1)} />
        )}
      </div>
    </div>
  );
}

function KidStep({ step, idx, total, onNext, onPrev }) {
  return (
    <div className="px-6 mt-4 flex-1 flex flex-col" data-testid="kid-step">
      <div className="flex gap-1.5">{Array.from({ length: total }).map((_, i) => <div key={i} className={`h-2 flex-1 rounded-full ${i <= idx ? "bg-amber-400" : "bg-white/70"}`} />)}</div>
      <p className="font-hand text-3xl text-sorted-ink mt-4" data-testid="kid-step-counter">Job {idx + 1} of {total}</p>
      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, scale: 0.95, x: 30 }} animate={{ opacity: 1, scale: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}
          className="mt-3 bg-white rounded-[2rem] shadow-soft overflow-hidden">
          {step.image_url ? (
            <img src={`${API_BASE}${step.image_url}`} alt="" className="w-full aspect-square object-cover" data-testid="kid-step-image" />
          ) : (
            <div className="w-full aspect-square bg-[#FFF6F1] flex items-center justify-center text-[120px]" data-testid="kid-step-emoji">{step.emoji}</div>
          )}
          <div className="p-5">
            <p className="font-heading text-2xl font-bold text-sorted-ink leading-snug" data-testid="kid-step-text">{step.text}</p>
            {step.grown_up && <p className="mt-2 inline-flex items-center gap-1 bg-[#FAD9CE] text-sorted-ink text-xs font-bold px-3 py-1.5 rounded-full" data-testid="kid-step-grownup"><ShieldAlert size={13} strokeWidth={2.5} /> Grown-up job — you can watch!</p>}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-3 flex items-center gap-3">
        <MillieAvatar size={40} float={false} mood={idx % 2 ? "happy" : "cooking"} />
        <p className="font-hand text-xl text-sorted-ink">{idx === 0 ? "Ready, set, cook!" : CHEERS[idx % CHEERS.length]}</p>
      </div>
      <div className="mt-auto pt-6 flex items-center gap-3">
        <button onClick={onPrev} disabled={idx === 0} data-testid="kid-prev" className="w-16 h-16 rounded-full bg-white text-sorted-ink flex items-center justify-center disabled:opacity-40 shadow-soft"><ArrowLeft size={24} strokeWidth={2.5} /></button>
        <button onClick={onNext} data-testid="kid-next" className="flex-1 bg-sorted-rose text-white font-bold py-5 text-lg rounded-full flex items-center justify-center gap-2">{idx === total - 1 ? "All done!" : "Next job"} <ArrowRight size={22} strokeWidth={2.5} /></button>
      </div>
    </div>
  );
}
